import type { ReactNode, Ref } from "react";
import { BadgePercent, Trash2, X } from "lucide-react";
import type { UnitType } from "@/features/quotation/types";
import {
  getQuotationBasePriceValue,
  PRICE_PER_UNIT_SUFFIX,
} from "@/features/quotation/unitLabels";
import { formatBRL } from "@/lib/timeAgo";

type LedgerItem = {
  product_name: string;
  supplier_name?: string;
  unit_type: UnitType;
  kg_per_box?: number;
  units_per_box?: number;
  price_per_box?: number;
  price_per_kg?: number;
  price_per_unit: number;
};

export function QuotationDetailLedgerRow({
  item,
  selected,
  source,
  actions,
  onEdit,
  editDisabled,
}: {
  item: LedgerItem;
  selected: boolean;
  source?: string;
  actions: ReactNode;
  onEdit?: () => void;
  editDisabled?: boolean;
}) {
  const pack =
    item.unit_type === "sc_kg"
      ? "Saco por kg"
      : item.unit_type === "cx_kg" || item.unit_type === "cx_un"
        ? "Caixa"
        : item.unit_type === "bdj"
          ? "Bandeja"
          : item.unit_type === "kg"
            ? "Kg"
            : "Unidade";
  const quantity = item.kg_per_box
    ? `${item.kg_per_box} kg`
    : item.units_per_box
      ? `${item.units_per_box} un`
      : "";
  const description = selected
    ? [item.supplier_name, quantity ? `${pack} · ${quantity}` : pack]
        .filter(Boolean)
        .join(" · ")
    : (source ?? (quantity ? `${pack} · ${quantity}` : pack));
  return (
    <article
      className={`lnk-admin-product-row ${selected ? "is-selected" : "is-pending"}`}
    >
      <div>
        {onEdit ? (
          <button
            type="button"
            className="lnk-product-name-action"
            aria-label={`Editar ${item.product_name}`}
            disabled={editDisabled}
            onClick={onEdit}
          >
            <strong>{item.product_name}</strong>
          </button>
        ) : (
          <strong>{item.product_name}</strong>
        )}
        <span>{description}</span>
      </div>
      <div>
        <b>
          {selected || source
            ? formatBRL(getQuotationBasePriceValue(item))
            : "—"}
        </b>
        <small>
          {selected || source
            ? `${formatBRL(item.price_per_unit)} / ${PRICE_PER_UNIT_SUFFIX[item.unit_type].toLowerCase()}`
            : "—"}
        </small>
      </div>
      <div className="lnk-admin-row-actions">{actions}</div>
    </article>
  );
}

export function QuotationOfferButton({
  active,
  disabled,
  onClick,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`quotation-offer-action ${active ? "is-offer" : ""}`}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
    >
      <BadgePercent size={16} />
      {active ? "Oferta ativa" : "Não é oferta"}
    </button>
  );
}

export function QuotationItemRemoveConfirmation({
  name,
  pending,
  confirmRef,
  onConfirm,
  onCancel,
}: {
  name: string;
  pending: boolean;
  confirmRef: Ref<HTMLButtonElement>;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="quotation-item-remove-confirmation"
      role="group"
      aria-label={`Confirmar exclusão de ${name}`}
    >
      <span>Excluir item?</span>
      <button
        ref={confirmRef}
        type="button"
        className="danger"
        disabled={pending}
        onClick={onConfirm}
      >
        <Trash2 size={16} /> Confirmar
      </button>
      <button type="button" disabled={pending} onClick={onCancel}>
        <X size={16} /> Cancelar
      </button>
    </div>
  );
}
